import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { HiOutlineChevronDown } from 'react-icons/hi2';
import Button, { type ButtonVariant } from '../../atoms/Button';
import Tooltip from '../../atoms/Tooltip';
import Menu from '../Menu';
import { useUi } from '../../provider/UiContext';
import type { IListElement } from '../../types/IListElement';
import './EmptyState.scss';

/** `primary` — залитая кнопка, `outline` — кнопка в тонкой рамке (`Button variant="outline-neutral"`), `link` —
 *  текстовая кнопка-ссылка (`Button variant="text"`). */
export type EmptyStateActionVariant = 'primary' | 'outline' | 'link';

/**
 * Действие пустого состояния. Тот же тип — у действий пустой таблицы (`DataTable filteredEmptyActions`): с `list`
 * кнопка открывает меню (`@srm/ui/menu`) и `onClick` не вызывает.
 */
export interface IEmptyStateAction {
    /** Подпись — полной фразой: «Создать поток». */
    label: string;
    onClick?: () => void;
    /** Значок перед подписью. */
    icon?: ReactNode;
    /** Вид; по умолчанию первое действие — `primary` (главное), остальные — `outline`. */
    variant?: EmptyStateActionVariant;
    /** Пункты меню: кнопка открывает меню (стрелка у подписи), а не выполняет `onClick`. */
    list?: IListElement[];
    /** Меню (`list`) открылось или закрылось — например, загрузить пункты при первом открытии. */
    onOpenChange?: (opened: boolean) => void;
    disabled?: boolean;
    /** Подсказка у кнопки — например, почему она недоступна («Выберите папку»). */
    hint?: ReactNode;
    /** Идёт действие: индикатор на кнопке, повтор заблокирован. У кнопки-меню — пункты ещё грузятся: скелетон в
     *  меню. */
    loading?: boolean;
    /** Переход внутри приложения вместо `onClick`: ссылка `UiProvider.linkComponent` (вид `link`). */
    href?: string;
}

const BUTTON_VARIANT: Record<EmptyStateActionVariant, ButtonVariant> = {
    primary: 'primary',
    outline: 'outline-neutral',
    link: 'text',
};

export interface IEmptyStateActionProps {
    action: IEmptyStateAction;
    variant: EmptyStateActionVariant;
    /** Класс кнопки или ссылки — раскладка места, где стоят действия. */
    className?: string;
    'data-testid'?: string;
}

/** Кнопка, кнопка-меню или ссылка действия пустого состояния — у `EmptyState` и у пустой таблицы. */
const EmptyStateAction = ({ action, variant, className, 'data-testid': testId }: IEmptyStateActionProps) => {
    const { linkComponent: Link } = useUi();
    const { label, onClick, icon, list, onOpenChange, disabled, hint, loading, href } = action;
    // ссылка ведёт дальше — стрелка после подписи, как у ссылок-переходов прототипа
    const arrow = variant === 'link' && !list ? <span aria-hidden>→</span> : undefined;

    let control: ReactNode;
    if (href && !list && !disabled) {
        control = (
            <Link to={href} className={clsx('rf-empty-state__link', className)} data-testid={testId}>
                {icon}
                {label}
                {arrow}
            </Link>
        );
    } else {
        const button = (
            <Button
                className={clsx(
                    'rf-empty-state__button',
                    variant === 'link' && 'rf-empty-state__button--link',
                    className,
                )}
                variant={BUTTON_VARIANT[variant]}
                size="sm"
                startAdornment={icon}
                endAdornment={list ? <HiOutlineChevronDown className="rf-empty-state__chevron" aria-hidden /> : arrow}
                disabled={disabled}
                loading={loading}
                onClick={list || loading ? undefined : onClick}
                data-testid={testId}
            >
                {label}
            </Button>
        );
        control = list ? (
            <Menu list={list} loading={loading} onOpenChange={onOpenChange} position="bottom" disabled={disabled}>
                {button}
            </Menu>
        ) : (
            button
        );
    }

    if (hint == null) return control;
    return (
        <Tooltip openDelay={100} background="black" position="bottom" label={hint}>
            {control}
        </Tooltip>
    );
};

export default EmptyStateAction;
